import Addendums from "components/Dashboard/DoctorDashboard/notes-and-forms/components/addendums/Addendums";
import CopyToClipboardButton from "components/copy-to-clipboard-button/CopyToClipboardButton";
import copyElementToClipboard from "notes-and-forms-v2/util/copyElementToClipboard";
import AtomicElementView, {
  AtomicElementViewForClipboard,
  ElementViewForClipboardProps,
  ElementViewProps,
} from "../atomic-element-view/AtomicElementView";
import styles from "./SectionView.module.scss";
import getClasses from "utils/getClasses";
import { useMemo } from "react";
import { useAddendums } from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/note-fill-in/contexts/AddendumsContext";

interface SectionViewProps extends ElementViewProps<TemplateSection> {
  sectionIndex: number;
  signatureData: TemplateData["signatures"];
  fieldsToShowAsMissing: string[];
  firstMissingElementRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export function SectionViewForClipboard({
  element,
  data,
}: ElementViewForClipboardProps<TemplateSection>) {
  return (
    <div>
      <h2>{element.title}</h2>
      <div>{element.description}</div>
      {element.children.map((childElement) => (
        <AtomicElementViewForClipboard
          key={childElement.id}
          element={childElement}
          data={data?.[childElement.id]}
        />
      ))}
    </div>
  );
}

export default function SectionView(props: SectionViewProps) {
  const {
    element,
    data,
    signatureData,
    onChange,
    onSignatureChange,
    isPrintMode,
    showCopyToClipboard,
    sectionIndex,
    theme = "default",
    fieldsToShowAsMissing,
    firstMissingElementRef,
  } = props;

  const { addendums, canAdd, addendum } = useAddendums();
  const showAddendums = useMemo(() => {
    return (
      canAdd ||
      addendum?.[element.id] ||
      addendums?.some((addendum) => addendum.addendum?.[element.id])
    );
  }, [element.id, addendums, canAdd, addendum]);

  return (
    <div className={styles.container}>
      <div
        className={getClasses(
          styles.item,
          theme === "session-review" && styles["session-review"],
        )}
      >
        <div
          className={getClasses(
            styles.title,
            !element.title.length && styles.hideWhenNotHovered,
          )}
        >
          <div>{element.title}</div>
          {showCopyToClipboard && (
            <div className={styles.hideWhenNotHovered}>
              <CopyToClipboardButton
                onClick={() => copyElementToClipboard(element, data)}
                size="new"
              />
            </div>
          )}
        </div>
        <div className={styles.description}>{element.description}</div>
      </div>
      {showAddendums && <Addendums id={element.id} />}
      <div className={styles.content}>
        {element.children.map((childElement) => (
          <AtomicElementView
            key={childElement.id}
            ref={
              childElement.id === fieldsToShowAsMissing[0]
                ? firstMissingElementRef
                : null
            }
            {...props}
            element={childElement}
            data={data?.[childElement.id]}
            signatureData={signatureData?.[childElement.id]}
            onChange={(newValue) =>
              onChange({ ...data, [childElement.id]: newValue })
            }
            onSignatureChange={onSignatureChange}
            showAsMissing={fieldsToShowAsMissing.includes(childElement.id)}
          />
        ))}
      </div>
      {isPrintMode && (
        <div className={styles.footer}>
          This is the end of the section {sectionIndex + 1}
        </div>
      )}
    </div>
  );
}
